import { useEffect, useRef, useState } from 'react';
import { Search, X, Package, Boxes, Layers, MapPin, Truck } from 'lucide-react';
import { api } from '../api';
import Miniatura from './Miniatura';
import { useCapa } from './navegacionReact';

// Una sola caja para encontrar cualquier cosa. En un almacén grande, quien
// busca casi nunca tiene el dato "correcto": tiene la serie grabada en la
// carcasa, el nombre del proveedor o media palabra del nombre. El backend
// resuelve por todos esos caminos; aquí solo se presenta y se navega.

const ICONO = {
  PRODUCTO: Package,
  PIEZA: Boxes,
  LOTE: Layers,
  UBICACION: MapPin,
  PROVEEDOR: Truck,
};

const ETIQUETA_TIPO = {
  PRODUCTO: 'Productos',
  PIEZA: 'Piezas',
  LOTE: 'Lotes',
  UBICACION: 'Ubicaciones',
  PROVEEDOR: 'Proveedores',
};

// A qué módulo lleva cada acción, y con qué contexto. Escanear un QR y
// encontrar por texto acaban en el MISMO sitio: es la misma pregunta.
// A dónde lleva «ver todos» de cada familia: al listado del módulo con el
// término ya puesto en su filtro. Solo las familias que TIENEN un listado
// paginado detrás — mandar a alguien a una pantalla que no filtra por eso
// sería peor que no ofrecer el enlace.
// Solo las familias que TIENEN un listado paginado con un filtro de texto
// detrás. Ubicaciones no lo tiene, así que no se ofrece el enlace: mandar
// a alguien a una pantalla que no filtra por lo que buscaba es peor que no
// ofrecerlo — parece que funcionó y no filtró nada.
const TODOS = {
  PRODUCTO: (q) => ['productos', { buscar: q }],
  PIEZA: (q) => ['activos', { buscar: q }],
};

const DESTINO = {
  FICHA_PRODUCTO: (r) => ['productos', { sku: r.datos.sku }],
  FICHA_PIEZA: (r) => ['escanear', { codigo: r.datos.serie }],
  LISTA_PRODUCTOS: (r) => ['productos', { proveedor: r.datos.proveedor }],
  LISTA_EXISTENCIAS: (r) => ['existencias', { ubicacion: r.datos.ubicacion }],
};

export default function BuscadorGlobal({ onIr }) {
  const [q, setQ] = useState('');
  const [datos, setDatos] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const caja = useRef(null);
  const termino = q.trim();
  // El panel de resultados (solo cuando se ve): atrás lo cierra.
  useCapa(abierto && termino.length >= 2, () => setAbierto(false));

  useEffect(() => {
    if (termino.length < 2) return;
    let vigente = true;
    const t = setTimeout(() => {
      api
        .get(`/busqueda?q=${encodeURIComponent(termino)}`)
        .then((d) => vigente && setDatos(d))
        .catch(() => vigente && setDatos({ consulta: termino, resultados: [], total: 0 }))
    }, 250);
    return () => { vigente = false; clearTimeout(t); };
  }, [termino]);

  // Los resultados solo valen si son de LO QUE HAY ESCRITO AHORA. Sin esta
  // comprobación, mientras se teclea se enseñarían los de la consulta
  // anterior — o un "no hay nada" que en realidad ya está obsoleto.
  const alDia = datos?.consulta === termino;
  const cargando = !alDia;

  // Cerrar al hacer clic fuera: el panel tapa contenido y estorbaría.
  useEffect(() => {
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);

  function ir(r) {
    const destino = DESTINO[r.accion];
    if (!destino) return;
    const [modulo, contexto] = destino(r);
    setAbierto(false);
    setQ('');
    setDatos(null);
    onIr(modulo, contexto);
  }

  const resultados = alDia ? datos.resultados : [];
  const grupos = Object.keys(ETIQUETA_TIPO)
    .map((tipo) => [tipo, resultados.filter((r) => r.tipo === tipo)])
    .filter(([, lista]) => lista.length > 0);

  return (
    <div ref={caja} className="relative flex-1 max-w-xl">
      <Search className="h-4 w-4 text-gray-500 absolute left-2.5 top-2.5 pointer-events-none" />
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setAbierto(true); }}
        onFocus={() => setAbierto(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setAbierto(false); e.currentTarget.blur(); }
          if (e.key === 'Enter' && resultados.length > 0) ir(resultados[0]);
        }}
        placeholder="Buscar SKU, serie, lote, proveedor, ubicación…"
        aria-label="Buscar en todo el sistema: SKU, serie, lote, proveedor o ubicación"
        className="w-full pl-8 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[var(--color-marca)] focus:ring-2 focus:ring-[var(--color-marca)] outline-none"
      />
      {q && (
        <button type="button" onClick={() => { setQ(''); setDatos(null); }}
                aria-label="Borrar la búsqueda"
                className="absolute right-2 top-2 text-gray-500 hover:text-gray-700 rounded
                           focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}

      {/* EL PANEL TIENE ANCHO MÍNIMO PROPIO (T-12). Antes usaba
          `left-0 right-0`, así que heredaba el ancho de la caja de
          búsqueda; y en pantallas medias (≈910 px) la caja queda
          espachurrada a ~100 px porque el bloque de la derecha no cede.
          El resultado: un desplegable de 100 px, ilegible. Ahora el panel
          se ancla al borde izquierdo y toma el MAYOR entre el ancho de la
          caja y 20rem, sin pasarse del ancho de la ventana. */}
      {abierto && termino.length >= 2 && (
        <div className="absolute left-0 top-11 w-[max(100%,20rem)] max-w-[calc(100vw-1.5rem)] bg-white border border-gray-200 rounded-xl shadow-xl max-h-[70vh] overflow-y-auto z-30">
          {cargando && resultados.length === 0 && (
            <p className="px-4 py-3 text-sm text-gray-500">Buscando…</p>
          )}

          {!cargando && resultados.length === 0 && (
            <div className="px-4 py-4 text-sm text-gray-500">
              Nada coincide con “{termino}”. Prueba con parte del nombre, el proveedor o el número de serie.
            </div>
          )}

          {grupos.map(([tipo, lista]) => {
            const g = (datos?.grupos || []).find((x) => x.tipo === tipo);
            const verTodo = TODOS[tipo];
            return (
              <div key={tipo}>
                <div className="px-3 pt-2 pb-1 flex items-baseline justify-between gap-2">
                  <span className="text-[11px] font-semibold text-gray-500 uppercase">
                    {ETIQUETA_TIPO[tipo]}
                  </span>
                  {/* CUÁNTOS HAY, no cuántos cupieron. Ver diez y no poder
                      distinguir «hay diez» de «hay 347, aquí van los
                      primeros» es lo que hacía inútil este buscador para
                      cualquier término común. */}
                  {g?.truncado && (
                    <span className="text-[11px] text-gray-500 shrink-0">
                      {lista.length} de {g.total}
                    </span>
                  )}
                </div>
                {lista.map((r) => <Fila key={`${r.tipo}-${r.clave}`} r={r} onIr={() => ir(r)} />)}
                {/* El desplegable no pagina a propósito: existe para
                    ENCONTRAR UNA COSA. Cuando hay más, el camino es el
                    listado del módulo, que sí pagina y sí filtra. */}
                {g?.truncado && verTodo && (
                  <button
                    type="button"
                    onClick={() => { setAbierto(false); onIr(...verTodo(termino)); }}
                    className="w-full text-left px-3 py-2 text-xs font-semibold text-[var(--color-marca)]
                               hover:bg-gray-50 border-t border-gray-100
                               focus:outline-none focus:bg-gray-50"
                  >
                    Ver los {g.total} en {ETIQUETA_TIPO[tipo].toLowerCase()} →
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Fila({ r, onIr }) {
  const Icono = ICONO[r.tipo] || Package;
  return (
    <button
      onClick={onIr}
      className="w-full text-left flex items-start gap-2.5 px-3 py-2 hover:bg-gray-50"
    >
      {/* Con foto se ve la foto; sin ella, el icono del tipo. Dos balatas
          del mismo eje son la misma linea de texto. */}
      {r.imagen
        ? <Miniatura src={r.imagen} nombre={r.titulo} tamano="sm" />
        : <Icono className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />}
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-gray-900 truncate">{r.titulo}</span>
        {r.detalle && <span className="block text-xs text-gray-500 truncate">{r.detalle}</span>}
      </span>
      {r.exacto && (
        <span className="text-[10px] font-semibold text-[var(--color-marca)] bg-blue-50 px-1.5 py-0.5 rounded shrink-0">
          exacto
        </span>
      )}
    </button>
  );
}
